import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { trans } from '../utils';

/**
 * Format timestamp to readable string
 * @param {Date} date
 * @returns {string}
 */
const formatTimestamp = (date) => {
  const now = new Date();
  const messageDate = new Date(date);
  
  // If same day, show time only
  if (messageDate.toDateString() === now.toDateString()) {
    return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  
  // If yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (messageDate.toDateString() === yesterday.toDateString()) {
    return `${trans('Yesterday')} ${messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  
  // Otherwise show full date
  return messageDate.toLocaleDateString([], { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Normalize content for markdown rendering
 * @param {string} content
 * @returns {string}
 */
const normalizeContent = (content) => {
  if (!content) return '';
  
  // Replace escaped newlines with actual newlines
  return content
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n');
};

/**
 * Message Bubble - Individual message display
 */
const MessageBubble = ({ message }) => {
  const isUser = message.role === 'user';
  const isError = message.isError;

  return (
    <div 
      className={`chat-message ${isUser ? 'user' : 'assistant'} ${isError ? 'error' : ''}`}
      role="article"
      aria-label={`${isUser ? trans('Your message') : trans('Assistant message')}`}
    >
      <div className="chat-message-bubble">
        {isUser ? (
          // User messages: simple text with line breaks
          normalizeContent(message.content).split('\n').map((line, index, arr) => (
            <React.Fragment key={index}>
              {line}
              {index < arr.length - 1 && <br />}
            </React.Fragment>
          ))
        ) : (
          // Assistant messages: full markdown support
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              // Custom table wrapper for horizontal scrolling
              table: ({ node, ...props }) => (
                <div className="chat-table-wrapper">
                  <table {...props} />
                </div>
              ),
              // Open links in new tab
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              )
            }}
          >
            {normalizeContent(message.content)}
          </ReactMarkdown>
        )}
      </div>
      
      {message.attachedContext && (
        <div className="chat-message-context">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
          </svg>
          {message.attachedContext.description || trans('Context attached')}
        </div>
      )}
      
      <span className="chat-message-timestamp">
        {formatTimestamp(message.timestamp)}
      </span>
    </div>
  );
};

export default MessageBubble;
